import { useParams } from "react-router-dom";
import {
  IGlobalPeriodContext,
  useGlobalCurrency,
  useReportBespoke,
  useSnapshot,
} from "@iliotech/data-wire";
import { EnabledColumnMap, IllioTable } from "src/main";
import React from "react";
import { GridCellProps } from "@progress/kendo-react-grid";

const DEFAULT_COLUMNS = Object.fromEntries(
  IllioTable.ColumnSets.PositionPageColumns.filter(
    (column) => column.isDefault
  ).map((col) => [col.field, { enabled: true, order: col.order || 1000 }])
);

// Fields that should be formatted with 2 decimal places
const PRICE_FIELDS = [
  "localPrice",
  "costPrice",
  "dirtyPrice",
  "underlyingSpotPrice",
  "strike",
];

// Format number with commas and specified decimal places
const localValueFormatter = (value: number, decimals: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) return "";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// Custom cell renderer for numbers
const createNumberCell =
  (field: string) =>
  (props: GridCellProps): React.ReactElement => {
    const value = props.dataItem[field];
    const isPriceField = PRICE_FIELDS.includes(field);
    const decimals = isPriceField ? 2 : 0;
    const displayValue =
      typeof value === "number"
        ? localValueFormatter(value, decimals)
        : value ?? "";

    return React.createElement(
      "td",
      { style: { textAlign: "left", ...props.style } },
      displayValue
    );
  };

export const usePositionsWidget = (cellId: string, pageId: string) => {
  const store = useReportBespoke();
  const allColumns = IllioTable.ColumnSets.PositionPageColumns.filter(
    (c) => c.field !== "action" && c.field !== "insights"
  );
  const { currency } = useGlobalCurrency();
  const columnSettings: EnabledColumnMap =
    store.pages[pageId].cells[cellId].extraOptions?.positionsWidgetSettings ||
    DEFAULT_COLUMNS;

  const setColumnSettings = (preferences: any) => {
    store.updateCell(pageId, cellId, "extraOptions", {
      ...store.pages[pageId].cells[cellId].extraOptions,
      positionsWidgetSettings: preferences,
    });
  };
  const columns = React.useMemo(() => {
    return allColumns
      .filter((column) => columnSettings[column.field]?.enabled)
      .sort((a, b) => (a.order || 900) - (b.order || 900))
      .sort(
        (a, b) =>
          (columnSettings[a.field]?.order ?? (a.order || 900)) -
          (columnSettings[b.field]?.order ?? (b.order || 900))
      )
      .map((item) => {
        const { renderer, ...rest } = item;
        return {
          ...rest,
          title: item.title?.replace("(£)", `(${currency})`),
          cell: createNumberCell(item.field),
        };
      });
  }, [columnSettings]);
  return { columnSettings, setColumnSettings, columns, allColumns };
};
